import { PhysicianEntity } from "@src/features/physicians";
import { StaffEntity } from "@src/features/staff";
import { Tours } from "@src/features/tourguide";
import { cn } from "@src/shared/lib/utils";
import { useActor } from "@src/shared/providers/actorProvider";

import {
  AuthenticatedAppHeaderSlot,
  AuthenticatedAppHeaderSlotProvider,
} from "./AuthenticatedAppHeader";
import type {
  AuthenticatedLayoutUserV2,
  AuthenticatedLayoutV2Props,
} from "./AuthenticatedLayout.types";
import {
  AuthenticatedMobileTopBar,
  AuthenticatedSidebar,
} from "./AuthenticatedSidebar";

function getLayoutUser(
  actor: ReturnType<typeof useActor>,
): AuthenticatedLayoutUserV2 {
  if (actor.type === "physician") {
    return {
      name: PhysicianEntity.displayName(actor.user) ?? "Physician",
      subtitle: PhysicianEntity.specialtyLabel(actor.user) ?? undefined,
      initials: PhysicianEntity.initials(actor.user) ?? "DR",
    };
  }

  return {
    name: StaffEntity.displayName(actor.user) ?? "Staff",
    subtitle: "Staff",
    initials: StaffEntity.initials(actor.user) ?? "ST",
  };
}

export function AuthenticatedLayoutV2({
  children,
  className,
  defaultSidebarCollapsed,
}: AuthenticatedLayoutV2Props) {
  const actor = useActor();
  const user = getLayoutUser(actor);

  return (
    <div
      className={cn(
        "flex h-dvh min-h-0 w-full overflow-hidden bg-background text-foreground pl-[var(--safe-area-left)] pr-[var(--safe-area-right)]",
        className,
      )}
    >
      <Tours />
      <AuthenticatedSidebar
        defaultCollapsed={defaultSidebarCollapsed}
        user={user}
      />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <AuthenticatedAppHeaderSlotProvider>
          <AuthenticatedMobileTopBar user={user} />
          <AuthenticatedAppHeaderSlot />
          <main className="min-h-0 flex-1 overflow-y-auto bg-background">
            {/* WebKit ignores padding-bottom on overflow:auto; keep inset on the content box. */}
            {/* h-full (not min-h-full) gives descendants a definite height so
                flex-1 min-h-0 subtrees cap and scroll internally. */}
            <div className="flex h-full flex-col pb-[var(--safe-area-bottom)]">
              {children}
            </div>
          </main>
        </AuthenticatedAppHeaderSlotProvider>
      </div>
    </div>
  );
}
